import React, { useEffect, useState } from "react";
import TelegramBackButton from "../../components/TelegramBackButton.jsx";
import NoResult from "../../assets/inbox_empty.png";
import defaultImage from "../../assets/default.png";
import { API_BASE_URL, API_KEY } from "../../config.js";
import { Link, useNavigate } from "react-router-dom";
import TelegramUser from "../../TelegramUser.js";
import LoadingAnim from "../../components/LoadingAnim.jsx";
import { useTranslation } from "react-i18next";
import PaymentModal from "./Payment/PaymentModal.jsx";
import "../../App.css";
import "./BookMark.css";

const ITEMS_PER_PAGE = 10;

const BookMark = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isHomePage = false;

  const [properties, setProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedReservationId, setSelectedReservationId] = useState(null);

  const fetchProperties = async (page) => {
    try {
      setIsLoading(true);

      const userData = {
        username: TelegramUser.username,
      };
      const initData = window.Telegram.WebApp.initData;
      const response = await fetch(
        `${API_BASE_URL}/user/api/v1/reservation/get/?page=${page}&page_size=${ITEMS_PER_PAGE}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": API_KEY,
            "X-Telegram-InitData": initData,
          },
          body: JSON.stringify(userData),
        }
      );
      const data = await response.json();
      console.log("Fetched properties:", data);

      const totalItems = data.count;

      setTotalPages(Math.ceil(totalItems / ITEMS_PER_PAGE));
      setProperties((prevProperties) => [...prevProperties, ...data.results]);
    } catch (error) {
      console.error("Failed to fetch properties:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProperties(page);
  }, [page]);

  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop >=
        document.documentElement.offsetHeight - 100
      ) {
        if (!isLoading && page < totalPages) {
          setPage((prevPage) => prevPage + 1);
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isLoading, page, totalPages]);

  const handlePropertyClick = (event, property) => {
    if (property.status === "Rejected") {
      event.preventDefault();
      window.Telegram.WebApp.showAlert(
        `Unauthorized: ${property.status} status`
      );
    } else if (property.status === "Approved") {
      navigate(`/approvedDetail/${property.id}`);
    } else {
      navigate(`/pendingDetail/${property.id}`, { state: { property } });
    }
  };

  const handleMakePayment = (reservationId) => {
    setSelectedReservationId(reservationId);
    setIsPaymentModalOpen(true);
  };

  const handleProceedPayment = (method) => {
    //alert(`Proceeding with ${method}`);
    setIsPaymentModalOpen(false);
  };

  return (
    <>
      <div style={{ display: "flex", alignItems: "center" }}>
        <TelegramBackButton isHomePage={isHomePage} />
      </div>

      <div className="p-4">
        <h3 className="text-sm font-bold">{t("Booked")}</h3>
        {isLoading && page === 1 ? (
          <LoadingAnim />
        ) : properties.length > 0 ? (
          properties.map((property) => (
            <div
              key={property.id}
              className="w-full cursor-pointer mb-4 rounded-lg overflow-hidden"
              onClick={(event) => handlePropertyClick(event, property)}
            >
              {/* Image Container */}
              <div className="property-image-container rounded-lg">
                <img
                  src={property?.house.postedBy?.tumble_image || defaultImage}
                  alt={property.house.title}
                  className="property-image"
                />
              </div>

              {/* Property Details */}
              <div className="property-details-container">
                <div className="flex items-start">
                  <div className="flex-2">
                    {/* Row 1: House Title and Rating */}
                    <div className="relative flex items-center">
                      <h3 className="text-md text-black font-bold">
                        {property.house.title}
                      </h3>

                      {/* Rating Section - Right End */}
                      <div className="absolute right-5 flex items-center text-yellow-400">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M12 2l3.09 6.26 6.91.99-5 4.87L18.18 22 12 18.27 5.82 22l1.18-7.88-5-4.87 6.91-.99L12 2z" />
                        </svg>
                        <span className="text-xs text-gray-800 ml-2">
                          {property.house.rating || "4.0"} / 5.0
                        </span>
                      </div>
                    </div>

                    {/* Row 2: Sub Description */}
                    <p className="text-xs text-gray-500 py-1 whitespace-nowrap overflow-x-auto scrollbar-hidden">
                      {property.house.sub_description}
                    </p>

                    {/* Row 3: PostedBy and Price */}
                    <div className="relative flex items-center py-1">
                      {/* Posted By Section - Left End */}
                      <p className="text-xs">
                        {t("Posted by")} @
                        <b>{property.house.postedBy.userAccount.first_name}</b>
                      </p>

                      {/* Price Section - Right End */}
                      <p className="absolute right-5 text-xs text-gray-500">
                        <b>
                          {property.house.price} {property.house.unit}
                        </b>{" "}
                        / {t("day")}
                      </p>
                    </div>

                    {/* Row 4: Booking Status */}
                    <div className="flex items-center gap-x-2 py-1">
                      <p className="text-xs text-gray-500">
                        {t("Booking Status")}:
                      </p>
                      <button
                        className={`text-xs text-white ${
                          property.status === "Approved"
                            ? "bg-green-600"
                            : property.status === "Rejected"
                              ? "bg-red-600"
                              : "bg-yellow-500"
                        }`}
                        style={{
                          borderRadius: "20px",
                          fontSize: "10px",
                          display: "inline-block",
                          padding: "0 4px",
                        }}
                      >
                        {property.status === "Approved"
                          ? t("Approved")
                          : property.status === "Rejected"
                            ? t("Rejected")
                            : t("Pending")}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Payment Button */}

                {property.status === "Waiting For payment" && (
                  <button
                    className="mt-2 w-full px-2 py-1 text-black bg-white border border-black rounded-xl text-xs"
                    onClick={(event) => {
                      event.stopPropagation(); // Prevent event bubbling
                      handleMakePayment(property.id);
                    }}
                  >
                    <i className="fa fa-dollar"></i> {t("Make Payment")}
                  </button>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="flex flex-col items-center justify-center text-center px-4 sm:px-0">
            <img
              src={NoResult}
              alt="no result found"
              className="mb-4 w-40 sm:w-22 md:w-30 lg:w-34 xl:w-32"
            />
            <span className="w-25">
              {t("You didn’t book any Properties. Search and book properties")}.
            </span>
            <br />
            <Link to="/">
              <button
                className="text-black text-xs py-3 px-10 rounded-xl"
                style={{ backgroundColor: "#ffff", border: "1px solid black" }}
              >
                {t("Search properties")}
              </button>
            </Link>
          </div>
        )}
      </div>
      {/* Payment Modal */}
      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        onProceed={handleProceedPayment}
        reservationId={selectedReservationId}
      />
    </>
  );
};

export default BookMark;
